/**
 * Persisted open/closed state for collapsible sidebar groups.
 *
 * Stored per group id under a single `charp.nav.expanded` key, so adding a
 * group costs no new storage key and clearing one entry cannot orphan another.
 *
 * Reading the current path takes priority over the persisted value: landing on
 * `/experiments/climate-loop` must open the group whatever the user last left
 * it as, otherwise a shared link shows the active page hidden inside a closed
 * row.
 */

import { useCallback, useState } from "react";

export const NAV_EXPANDED_STORAGE_KEY = "charp.nav.expanded";

type ExpandedState = Record<string, boolean>;

/**
 * `localStorage` is absent during SSR and throws in Safari private browsing, so
 * every access is guarded and failures degrade to "not persisted" rather than
 * taking the sidebar down with them.
 */
function readStored(): ExpandedState {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(NAV_EXPANDED_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        ([, value]) => typeof value === "boolean",
      ),
    ) as ExpandedState;
  } catch {
    return {};
  }
}

function writeStored(state: ExpandedState): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(NAV_EXPANDED_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Quota exceeded or storage disabled — the nav still works, it just forgets.
  }
}

export interface UseNavExpandedResult {
  /** Whether a group should render open, given whether it contains the active page. */
  isExpanded: (groupId: string, containsActivePath: boolean) => boolean;
  /** Toggle a group and persist the result. */
  toggle: (groupId: string, containsActivePath: boolean) => void;
}

export function useNavExpanded(): UseNavExpandedResult {
  // Read once on mount. The store is only written by this hook, so there is no
  // cross-tab sync to worry about beyond a reload picking up the latest value.
  const [expanded, setExpanded] = useState<ExpandedState>(readStored);

  const isExpanded = useCallback(
    (groupId: string, containsActivePath: boolean) => {
      // An explicit choice wins, but only while it does not hide the page the
      // user is actually on.
      if (containsActivePath) return expanded[groupId] !== false;
      return expanded[groupId] === true;
    },
    [expanded],
  );

  const toggle = useCallback((groupId: string, containsActivePath: boolean) => {
    setExpanded((previous) => {
      const currentlyOpen = containsActivePath
        ? previous[groupId] !== false
        : previous[groupId] === true;
      const next = { ...previous, [groupId]: !currentlyOpen };
      writeStored(next);
      return next;
    });
  }, []);

  return { isExpanded, toggle };
}
